import React, { RefObject, useCallback, useRef, useState } from 'react';

export const useGrid = <T extends HTMLElement>(width: number, height: number): {
    selectedIndex: number,
    onKeyDown: React.HTMLProps<HTMLElement>['onKeyDown'],
    ref: RefObject<T>,
} => {
    const gridRef = useRef<T>(null);
    const [ selectedIndex, setSelectedIndex ] = useState(0);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        let updateFocus = false;
        if (e.key === 'ArrowUp') {
            setSelectedIndex(current => Math.max(0, current - width));
            updateFocus = true;
        } else if (e.key === 'ArrowDown') {
            setSelectedIndex(current => Math.min(height * width - 1, current + width));
            updateFocus = true;
        } else if (e.key === 'ArrowRight') {
            setSelectedIndex(current => {
                const row = Math.floor(current / width);
                return Math.min(current + 1, (row + 1) * width - 1);
            });
            updateFocus = true;
        } else if (e.key === 'ArrowLeft') {
            setSelectedIndex(current => {
                const row = Math.floor(current / width);
                return Math.max(current - 1, row * width);
            });
            updateFocus = true;
        } else if (e.key === 'Home') {
            if (e.ctrlKey) {
                setSelectedIndex(0);
            } else {
                setSelectedIndex(current => Math.floor(current / width) * width);
            }
            updateFocus = true;
        } else if (e.key === 'End') {
            if (e.ctrlKey) {
                setSelectedIndex(width * height - 1);
            } else {
                setSelectedIndex(current => (Math.floor(current / width) + 1) * width - 1);
            }
            updateFocus = true;
        }

        if (updateFocus) {
            window.setTimeout(() => {
                const button = gridRef.current?.querySelector('[tabindex="0"]');
                if (button instanceof HTMLElement) {
                    button.focus();
                }
            });
        }
    }, [ width, height ]);

    return {
        selectedIndex,
        ref: gridRef,
        onKeyDown: handleKeyDown,
    };
};
